import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import i18next from 'i18next'

import { error, success } from 'src/components/system_wide/notification'
import axios from 'src/lib/axios'

const { t } = i18next

const initialState = {
  total: 0,
  dealers: [],
  all_dealers: [],
  errors: {},
  show_form: false,
  current_dealer: {
    id: null,
    business_name: '',
    piva: '',
    phone: '',
    commercial_email: '',
    administrative_email: '',
    address: '',
    city: '',
    province: '',
    cap: '',
    region: '',
    country: '',
    lat: null,
    lng: null,
    place_id: '',
    merchandise_category_id: null,
    agent_id: null,
    sig_fullname: '',
    sig_ssn: '',
    subcode: '',
    note: '',
    contacts: [],
    dealer_brands: [],
    agent: null,
    merchandise_category: null,
  },
  regions_provinces: {},
  dealers_appointments: [],
}

export const getDealers = createAsyncThunk(
  'dealers/list',
  async (
    {
      offset,
      limit,
      sortData,
      filter,
      status_filter,
      subcode_filter,
      status_data_filter,
      filtered_brands,
      filtered_users,
      filtered_categories,
    },
    thunkApi
  ) => {
    try {
      let { total: currentTotal } = thunkApi.getState().dealers
      if (offset > currentTotal) return

      let { sort_column, sort_direction } = sortData
      let response = await axios({
        url: `/api/dealers`,
        params: {
          offset,
          limit,
          sort_column,
          sort_direction,
          filter,
          status_filter,
          subcode_filter,
          status_data_filter,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_categories: filtered_categories.join(','),
        },
      })
      let { total, dealers } = response.data
      return { total, dealers, reset: offset === 0 }
    } catch (err) {
      error(t('store.dealers.get_dealers_error', 'Impossibile recuperare lista dei dealers'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getAllDealers = createAsyncThunk(
  'dealers/listAll',
  async ({ filter, status_filter, filtered_brands, filtered_users, avoid_duplicates = false, date }, thunkApi) => {
    try {
      let response = await axios({
        url: `/api/dealers/all`,
        params: {
          filter,
          status_filter,
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          avoid_duplicates,
          date,
        },
      })
      let { dealers } = response.data
      return dealers
    } catch (err) {
      error(t('store.dealers.get_dealers_error', 'Impossibile recuperare lista dei dealers'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const createDealer = createAsyncThunk('dealers/create', async ({ dealer }, thunkApi) => {
  try {
    await axios({
      url: `/api/dealers`,
      method: 'post',
      data: { ...dealer },
    })
    success(t('store.dealers.create_dealer_success', 'Dealer creato con successo'))
    thunkApi.dispatch(resetCurrentDealer())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const updateDealer = createAsyncThunk('dealers/update', async ({ dealer }, thunkApi) => {
  try {
    await axios({
      url: `/api/dealers/${dealer.id}`,
      method: 'patch',
      data: { ...dealer },
    })
    success(t('store.dealers.update_dealer_success', 'Dealer aggiornato con successo'))
    thunkApi.dispatch(resetCurrentDealer())
    thunkApi.dispatch(toggleShowForm())
  } catch (err) {
    return thunkApi.rejectWithValue(err?.response?.data ?? {})
  }
})

export const deleteDealer = createAsyncThunk('dealers/delete', async ({ id }, thunkApi) => {
  try {
    await axios({ url: `/api/dealers/${id}`, method: 'delete' })
    success(t('store.dealers.delete_dealer_success', 'Dealer eliminato con successo'))
    thunkApi.dispatch(resetCurrentDealer())
  } catch (err) {
    error(t('store.dealers.delete_dealer_error', 'Impossibile eliminare il dealer'))
    return thunkApi.rejectWithValue()
  }
})

export const getRegionsProvinces = createAsyncThunk(
  'dealers/getRegionsProvinces',
  async ({ filtered_brands, filtered_users, status_filter }, thunkApi) => {
    try {
      let response = await axios({
        url: `/api/dealers/regions_provinces`,
        method: 'GET',
        params: {
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          status_filter,
        },
      })
      return response.data
    } catch (err) {
      error(t('store.dealers.get_regions_provinces_error', 'Impossibile recuperare la lista delle regioni e province'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const getDealersAppointments = createAsyncThunk(
  'dealers/getDealersAppointments',
  async (
    {
      from,
      to,
      filtered_appointment_types,
      filtered_brands,
      filtered_users,
      filtered_statuses,
      filtered_regions,
      filtered_provinces,
    },
    thunkApi
  ) => {
    try {
      let response = await axios({
        url: `/api/dealers/appointments`,
        method: 'GET',
        params: {
          from,
          to,
          filtered_appointment_types: filtered_appointment_types.join(','),
          filtered_brands: filtered_brands.join(','),
          filtered_users: filtered_users.join(','),
          filtered_statuses: filtered_statuses.join(','),
          filtered_regions: encodeURIComponent(filtered_regions.join(',')),
          filtered_provinces: encodeURIComponent(filtered_provinces.join(',')),
        },
      })
      return response.data
    } catch (err) {
      error(t('store.dealers.get_dealers_appointments_error', 'Impossibile recuperare le visite dei dealers'))
      return thunkApi.rejectWithValue()
    }
  }
)

export const dealersSlice = createSlice({
  name: 'dealers',
  initialState,

  reducers: {
    setCurrentDealer: (state, action) => {
      const {
        id,
        business_name,
        piva,
        phone,
        commercial_email,
        administrative_email,
        address,
        city,
        province,
        cap,
        region,
        country,
        lat,
        lng,
        place_id,
        merchandise_category_id,
        agent_id,
        sig_fullname,
        sig_ssn,
        subcode,
        note,
        contacts,
        dealer_brands,
        agent,
        merchandise_category,
      } = action.payload

      state.current_dealer = {
        id,
        business_name,
        piva,
        phone,
        commercial_email,
        administrative_email,
        address,
        city,
        province,
        cap,
        region,
        country,
        lat,
        lng,
        place_id,
        merchandise_category_id,
        agent_id,
        sig_fullname,
        sig_ssn,
        subcode,
        note,
        contacts,
        dealer_brands,
        agent,
        merchandise_category,
      }
    },
    updateErrors: (state, action) => {
      state.errors = action.payload.errors
    },
    toggleShowForm: state => {
      state.show_form = !state.show_form
    },
    resetCurrentDealer: state => {
      state.current_dealer = initialState.current_dealer
    },
    resetDealers: () => initialState,
  },

  extraReducers: {
    [createDealer.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [updateDealer.rejected]: (state, action) => {
      state.errors = action.payload
    },
    [getDealers.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { total, dealers } = action.payload
      state.total = total
      state.dealers = action.payload.reset ? [...dealers] : [...state.dealers, ...dealers]
    },
    [getDealers.rejected]: state => {
      state.total = initialState.total
      state.dealers = initialState.dealers
    },
    [getAllDealers.fulfilled]: (state, action) => {
      state.all_dealers = action.payload
    },
    [getAllDealers.rejected]: (state, action) => {
      state.all_dealers = initialState.all_dealers
    },
    [getRegionsProvinces.fulfilled]: (state, action) => {
      if (!action?.payload) return
      state.regions_provinces = action.payload
    },
    [getDealersAppointments.fulfilled]: (state, action) => {
      if (!action?.payload) return
      let { dealers_appointments } = action.payload
      state.dealers_appointments = dealers_appointments.filter(da => da.lat && da.lng)
    },
    [getDealersAppointments.rejected]: state => {
      state.dealers_appointments = initialState.dealers_appointments
    },
  },
})

export const { setCurrentDealer, updateErrors, resetCurrentDealer, toggleShowForm, resetDealers } = dealersSlice.actions
export default dealersSlice.reducer
